import React, { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { useTranslation } from "react-i18next";

export default function About() {
  const { t } = useTranslation();
  const tx = {
    tag: t("about.tag"),
    lines: t("about.lines", { returnObjects: true }),
    about_tag: t("about.panelTag"),
    about_text: t("about.panelText"),
  };
  const lineRefs = useRef([]);
  const aboutPanelRef = useRef(null);
  const gridRef = useRef(null);
  const stickyRef = useRef(null);
  const [activeLine, setActiveLine] = useState(0);

  // Instead of revealing every line and leaving it fully bright forever
  // (which piles up into one big block of text as you scroll), only the
  // line nearest the logo's current height stays at full emphasis — lines
  // already scrolled past dim down, lines not reached yet stay faint.
  //
  // The logo itself isn't `position:sticky` (which freezes it the moment
  // it locks — reads as "stuck," not "with you"). Instead it's positioned
  // absolutely and its offset is driven directly by how far you've read
  // through the section, so it travels smoothly from the top of the
  // story down to the last line — accompanying the read rather than
  // parking in one spot. Still a plain scroll listener, no scroll capture.
  useEffect(() => {
    const handleScroll = () => {
      const grid = gridRef.current;
      const sticky = stickyRef.current;

      // Aim the logo at the vertical center of the viewport (its own
      // center, not just its top edge) instead of just below the navbar.
      const logoHeight = sticky ? sticky.offsetHeight : 0;
      const navClearance = window.innerHeight / 2 - logoHeight / 2;
      const focusY = window.scrollY + navClearance;

      // Move the logo: 0% through the section = top, 100% = flush with
      // the bottom, clamped so it never leaves the section's bounds.
      // Mobile stacks everything normally (no travel effect there).
      if (grid && sticky) {
        if (window.innerWidth <= 768) {
          sticky.style.transform = "";
        } else {
          const gridRect = grid.getBoundingClientRect();
          const gridTop = window.scrollY + gridRect.top;
          const travel = Math.max(0, grid.offsetHeight - sticky.offsetHeight);
          const progress = travel === 0 ? 0 : Math.min(1, Math.max(0, (focusY - gridTop) / travel));
          sticky.style.transform = `translateY(${progress * travel}px)`;
        }
      }

      // Highlight whichever line is nearest the logo's current height.
      let closest = 0;
      let closestDist = Infinity;
      lineRefs.current.forEach((el, i) => {
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const centerY = window.scrollY + rect.top + rect.height / 2;
        const dist = Math.abs(centerY - focusY);
        if (dist < closestDist) {
          closestDist = dist;
          closest = i;
        }
      });
      setActiveLine(closest);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll);
    handleScroll();
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [tx.lines.length]);

  // About panel fade in on scroll
  useEffect(() => {
    const panel = aboutPanelRef.current;
    if (!panel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          gsap.fromTo(
            panel,
            { opacity: 0, y: 60 },
            { opacity: 1, y: 0, duration: 1, ease: "power3.out" }
          );
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    observer.observe(panel);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      {/* PART 1 — Editorial narrative: brand mark stays put while the
          story scrolls past it, no wheel/scroll capture. */}
      <section className="about-editorial">
        <div className="about-editorial__grid" ref={gridRef}>

          <div className="about-editorial__sticky">
            <div className="about-editorial__sticky-inner" ref={stickyRef}>
              <img
                src="/KMCLogo.jpg"
                alt="KMC Health"
                className="about-editorial__logo"
              />
              <span className="about-editorial__tag">{tx.tag}</span>
            </div>
          </div>

          <div className="about-editorial__flow">
            {tx.lines.map((line, i) => (
              <p
                key={i}
                ref={(el) => (lineRefs.current[i] = el)}
                className={`about-editorial__line ${
                  i === tx.lines.length - 1 ? "about-editorial__line--accent" : ""
                } ${
                  i === activeLine ? "is-active" : i < activeLine ? "is-passed" : "is-pending"
                }`}
              >
                {line.split("\n").map((l, j, arr) => (
                  <span key={j}>
                    {l}
                    {j < arr.length - 1 && <br />}
                  </span>
                ))}
              </p>
            ))}
          </div>

        </div>
      </section>

      {/* PART 2 — About content */}
      <section
        ref={aboutPanelRef}
        className="about-panel"
        style={{ opacity: 0 }}
      >
        <div className="about-panel__inner">

          <div className="about-panel__left">

          <div className="about-panel__logo-wrap">
            <img
              src="/KMCLogo.jpg"
              alt="KMC Health"
              className="about-panel__logo"
            />
          </div>

        </div>

          <div className="about-panel__right">
            <span className="about-panel__tag">{tx.about_tag}</span>
            <p className="about-panel__text">{tx.about_text}</p>
          </div>

        </div>
      </section>
    </>
  );
}
